/**
 * HR Holidays — Phase 3.1 redesign (mirrors Allowance Master UX).
 *
 *   - Year selector + list of holidays for that year
 *   - "วันหยุดธนาคารไทย" suggestion chips — fixed-date ones add in one
 *     tap; lunar ones (มาฆบูชา/วิสาขบูชา/อาสาฬหบูชา/เข้าพรรษา) open the
 *     modal pre-filled so HR confirms the date against the lunar
 *     calendar that year
 *   - Counter X / 13 (Thai LPA minimum traditional holidays) with a
 *     warning when under 13
 */
import { useState, useEffect, useRef } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useHolidays, useUpsertHoliday, useDeleteHoliday } from '../../hooks';
import { CalendarPlus, X, Trash2, Sparkles, AlertTriangle, Check, Lock } from 'lucide-react';
import { useToast } from '../../components/ui/toast';
import { useConfirm } from '../../components/ui/confirm';

// วันแรงงาน (1 พ.ค.) is the one holiday Thai LPA §29 makes mandatory —
// auto-added per year and not deletable.
const MANDATORY_NAME = 'วันแรงงานแห่งชาติ';

interface Holiday {
  id: string;
  date: string;
  name: string;
  type: 'public' | 'company' | 'substitute' | 'compensatory';
  applies_to: string;
  auto_generated?: boolean;
}

const MIN_HOLIDAYS = 13;

const typeLabel: Record<string, string> = {
  public: 'วันหยุดราชการ',
  company: 'วันหยุดบริษัท',
  substitute: 'วันหยุดชดเชย',
  compensatory: 'วันหยุดชดเชย',
};

interface HolidaySuggestion {
  name: string;
  month?: number;
  day?: number;
  lunar?: boolean;
  approxMonth?: number;
  approxDay?: number;
}
const THAI_HOLIDAYS: HolidaySuggestion[] = [
  { name: 'วันขึ้นปีใหม่', month: 1, day: 1 },
  { name: 'วันมาฆบูชา', lunar: true, approxMonth: 3, approxDay: 3 },
  { name: 'วันจักรี', month: 4, day: 6 },
  { name: 'วันสงกรานต์ (13)', month: 4, day: 13 },
  { name: 'วันสงกรานต์ (14)', month: 4, day: 14 },
  { name: 'วันสงกรานต์ (15)', month: 4, day: 15 },
  { name: 'วันแรงงานแห่งชาติ', month: 5, day: 1 },
  { name: 'วันฉัตรมงคล', month: 5, day: 4 },
  { name: 'วันวิสาขบูชา', lunar: true, approxMonth: 5, approxDay: 31 },
  { name: 'วันเฉลิมฯ สมเด็จพระราชินี', month: 6, day: 3 },
  { name: 'วันอาสาฬหบูชา', lunar: true, approxMonth: 7, approxDay: 29 },
  { name: 'วันเข้าพรรษา', lunar: true, approxMonth: 7, approxDay: 30 },
  { name: 'วันเฉลิมฯ ร.10', month: 7, day: 28 },
  { name: 'วันแม่แห่งชาติ', month: 8, day: 12 },
  { name: 'วันคล้ายวันสวรรคต ร.9', month: 10, day: 13 },
  { name: 'วันปิยมหาราช', month: 10, day: 23 },
  { name: 'วันพ่อแห่งชาติ / วันชาติ', month: 12, day: 5 },
  { name: 'วันรัฐธรรมนูญ', month: 12, day: 10 },
  { name: 'วันสิ้นปี', month: 12, day: 31 },
];

function iso(year: number, m: number, d: number): string {
  return `${year}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}
function thaiMonth(m: number): string {
  return ['', 'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'][m] ?? '';
}

function PrefilledModal({ initial, onClose }: { initial: { date: string; name: string }; onClose: () => void }) {
  const upsert = useUpsertHoliday();
  const toast = useToast();
  const [date, setDate] = useState(initial.date);
  const [name, setName] = useState(initial.name);

  async function save() {
    if (!date || !name) { toast.error('กรอกวันที่ + ชื่อ'); return; }
    try {
      await upsert.mutateAsync({ date, name, type: 'public', applies_to: 'all' });
      onClose();
    } catch (e) { toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end lg:items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl p-4">
        <div className="flex justify-between items-center mb-3">
          <h2 className="font-semibold">เพิ่มวันหยุด</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="label">ชื่อวันหยุด</label>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label className="label">วันที่ (ตรวจสอบปฏิทินจันทรคติให้ตรงปี)</label>
            <input type="date" className="input" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <button onClick={save} disabled={upsert.isPending} className="btn-primary w-full">
            {upsert.isPending ? 'กำลังบันทึก…' : 'เพิ่ม'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Content-only version used by the Time hub. */
export function HRHolidaysContent() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const { data, isLoading } = useHolidays(year) as { data: Holiday[] | undefined; isLoading: boolean };
  const upsert = useUpsertHoliday();
  const remove = useDeleteHoliday();
  const toast = useToast();
  const confirm = useConfirm();
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [modal, setModal] = useState<{ date: string; name: string } | null>(null);

  const sorted = [...(data ?? [])].sort((a, b) => a.date.localeCompare(b.date));
  const existingNames = new Set(sorted.map((h) => h.name));
  // Mandatory วันแรงงาน is added automatically and hidden from the
  // suggestion list (it's not optional).
  const available = THAI_HOLIDAYS.filter((h) => !existingNames.has(h.name) && h.name !== MANDATORY_NAME);
  // The 13-minimum counts HR-declared holidays only; auto comp days are extra.
  const count = sorted.filter((h) => !h.auto_generated).length;

  // Auto-ensure วันแรงงาน exists for the selected year (once per year).
  const ensuredRef = useRef<Set<number>>(new Set());
  useEffect(() => {
    if (isLoading || !data) return;
    if (existingNames.has(MANDATORY_NAME)) return;
    if (ensuredRef.current.has(year)) return;
    ensuredRef.current.add(year);
    upsert.mutate({ date: iso(year, 5, 1), name: MANDATORY_NAME, type: 'public', applies_to: 'all' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, data, year]);

  async function addFixed(h: HolidaySuggestion) {
    try {
      await upsert.mutateAsync({ date: iso(year, h.month!, h.day!), name: h.name, type: 'public', applies_to: 'all' });
    } catch (e) { toast.error(`เพิ่มไม่สำเร็จ: ${e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'}`); }
  }
  function addLunar(h: HolidaySuggestion) {
    setModal({ date: iso(year, h.approxMonth ?? 1, h.approxDay ?? 1), name: h.name });
  }

  return (
    <>
      <div className="flex items-center justify-between mb-3 gap-2">
        <p className="text-sm text-gray-600">กำหนดวันหยุดประจำปี — ตามกฎหมายอย่างน้อย 13 วัน/ปี</p>
        <div className="flex items-center gap-1.5 shrink-0">
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="rounded-lg border border-gray-300 px-2 py-1 text-xs">
            {[0, 1, 2].map((i) => {
              const y = currentYear + 1 - i;
              return <option key={y} value={y}>{y + 543}</option>;
            })}
          </select>
          <button onClick={() => setShowSuggestions((v) => !v)} className="btn-secondary btn-slim">
            <Sparkles className="w-3.5 h-3.5" /> วันหยุดธนาคาร
          </button>
          <button onClick={() => setModal({ date: iso(year, 1, 1), name: '' })} className="btn-primary btn-slim">
            <CalendarPlus className="w-3.5 h-3.5" /> เพิ่มเอง
          </button>
        </div>
      </div>
      <div className={`card p-3 mb-4 flex items-center gap-2 ${count < MIN_HOLIDAYS ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
        {count < MIN_HOLIDAYS ? <AlertTriangle className="w-5 h-5 text-amber-600" /> : <Check className="w-5 h-5 text-green-600" />}
        <div className="text-sm">
          <span className="font-semibold">{count} / {MIN_HOLIDAYS} วัน</span>
          {count < MIN_HOLIDAYS
            ? <span className="text-amber-800"> — ยังขาดอีก {MIN_HOLIDAYS - count} วัน (กฎหมายขั้นต่ำ 13 วัน/ปี)</span>
            : <span className="text-green-700"> — ครบตามกฎหมายแล้ว</span>}
        </div>
      </div>

      {showSuggestions && (
        <div className="card p-3 mb-4 bg-brand-50/40 border-brand-200">
          <div className="text-sm font-semibold mb-2 flex items-center gap-1">
            <Sparkles className="w-4 h-4 text-brand-600" /> วันหยุดธนาคารไทย ปี {year + 543} — แตะเพื่อเพิ่ม
          </div>
          {available.length === 0 ? (
            <p className="text-xs text-gray-500">เพิ่มครบทุกวันหยุดมาตรฐานแล้ว 🎉</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {available.map((h) => (
                <button
                  key={h.name}
                  onClick={() => (h.lunar ? addLunar(h) : addFixed(h))}
                  title={h.lunar ? 'วันพระจันทรคติ — กดเพื่อยืนยันวันที่' : iso(year, h.month!, h.day!)}
                  className="text-xs border border-brand-300 bg-white rounded-full px-3 py-1.5 hover:bg-brand-50 transition"
                >
                  {h.lunar ? '🌙 ' : ''}{h.name}
                  {!h.lunar && <span className="text-gray-400 ml-1">{h.day}/{h.month}</span>}
                </button>
              ))}
            </div>
          )}
          <p className="text-[10px] text-gray-500 mt-2">🌙 = วันพระ (จันทรคติ) เปลี่ยนทุกปี — กดแล้วยืนยันวันที่อีกครั้ง</p>
        </div>
      )}

      {isLoading ? (
        <p className="py-8 text-center text-gray-500">กำลังโหลด…</p>
      ) : sorted.length === 0 ? (
        <p className="py-8 text-center text-gray-500">ยังไม่มีวันหยุดปี {year + 543} — กด "วันหยุดธนาคาร"</p>
      ) : (
        <div className="card divide-y divide-gray-100">
          {sorted.map((h) => (
            <div key={h.id} className={`flex items-center gap-3 p-3 ${h.auto_generated ? 'bg-amber-50/60' : ''}`}>
              <div className="text-center shrink-0 w-12">
                <div className="text-lg font-bold leading-none">{Number(h.date.slice(8, 10))}</div>
                <div className="text-[10px] text-gray-500">{thaiMonth(Number(h.date.slice(5, 7)))}</div>
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm">{h.name}</div>
                <div className="text-xs text-gray-500">{h.date}{h.applies_to !== 'all' ? ` · ${h.applies_to}` : ''}</div>
              </div>
              <span className={`text-[10px] badge ${h.auto_generated ? 'bg-amber-100 text-amber-700' : 'badge-info'}`}>{typeLabel[h.type] ?? h.type}</span>
              {h.name === MANDATORY_NAME || h.auto_generated ? (
                <span className="p-1 text-gray-300 shrink-0" title={h.auto_generated ? 'วันหยุดชดเชย สร้างอัตโนมัติ' : 'วันหยุดบังคับตามกฎหมาย — ลบไม่ได้'}>
                  <Lock className="w-4 h-4" />
                </span>
              ) : (
                <button onClick={async () => { if (await confirm({ title: `ลบ "${h.name}"?`, danger: true, confirmText: 'ลบ' })) await remove.mutateAsync(h.id); }} className="p-1 text-gray-400 hover:text-red-600 shrink-0">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {modal && <PrefilledModal initial={modal} onClose={() => setModal(null)} />}
    </>
  );
}

/** Standalone page kept for legacy /hr/holidays (now redirects via Time hub). */
export function HRHolidays() {
  return (
    <AdminLayout title="วันหยุดบริษัท">
      <HRHolidaysContent />
    </AdminLayout>
  );
}
